import { ExpandMore, Replay, Download } from '@mui/icons-material';
import { Box, Accordion, AccordionDetails, AccordionSummary, Divider, Theme, Typography, useMediaQuery } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { LoadingButton } from '@mui/lab';
import { FC, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { Table } from '../../../components';
import { formatDate, downloadFile, formatShortFriendlyDateWithTime, formatMoney } from '../../../helpers';
import { resendACHNotification, createNewACHNotification, adminDownloadACHNotificationPDF } from '../../../fetch';
import { IACHNotificationBatch, IACHBatchNotificationEmail } from '../../../models';
import { NotificationTableResponsiveComponent } from './ACHNotificationTableResponsive';

interface IACHResponsiveComponent {
  original: IACHNotificationBatch;
}

export const ACHResponsiveComponent: FC<IACHResponsiveComponent> = ({ original }) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [isSending, setSending] = useState<{
    [id: string | number]: boolean;
  }>({});
  const [isDownloading, setDownloading] = useState<{
    [id: string | number]: boolean;
  }>({});
  const { batch, created, batchEmails, batchPaymentNotificationId, batchTotal, totalVendors, totalPayments, batchDate } = original;

  const handleResendBatchEmails = async (id: string | number) => {
    const confirm = window.confirm('Are you sure you want to resend this batch of emails?');
    if (confirm) {
      setSending({
        [id]: true
      });
      try {
        const res = await createNewACHNotification({
          batchPaymentNotificationId: Number(id)
        });
        if (res?.Detail) {
          return enqueueSnackbar(res?.Detail ?? `Error sending emails, please try again.`, {
            variant: 'error'
          });
        }
        enqueueSnackbar(`Emails Sent!`, {
          variant: 'success'
        });
      } catch (error) {
        enqueueSnackbar(`Error sending emails, please try again.`, {
          variant: 'error'
        });
      } finally {
        setSending({
          [id]: false
        });
      }
    }
  };

  const handleResendEmail = async (id: string | number) => {
    const confirm = window.confirm('Are you sure you want to resend this email?');
    if (confirm) {
      setSending({
        [id]: true
      });
      try {
        await resendACHNotification(id);
        enqueueSnackbar(`Email Sent!`, {
          variant: 'success'
        });
      } catch (error) {
        enqueueSnackbar(`Error sending email, please try again.`, {
          variant: 'error'
        });
      } finally {
        setSending({
          [id]: false
        });
      }
    }
  };

  const handleDownloadPDF = async (achData: IACHBatchNotificationEmail) => {
    setDownloading({
      [achData.batchNotificationEmailId]: true
    });
    try {
      const res = await adminDownloadACHNotificationPDF(batch, achData.vendorSageId);

      if (res?.Detail) {
        enqueueSnackbar(`Error, ${res.Detail}`, {
          variant: 'error'
        });
      } else if (res && res.type.includes('application/pdf')) {
        return downloadFile(res, achData.emailSubject);
      }

      enqueueSnackbar(`Error downloading pdf, please try again.`, {
        variant: 'error'
      });
    } catch (error) {
      enqueueSnackbar(`Error downloading pdf, please try again.`, {
        variant: 'error'
      });
    } finally {
      setDownloading({
        [achData.batchNotificationEmailId]: false
      });
    }
  };

  const columns = useMemo(() => {
    return [
      {
        Header: 'Vendor',
        accessor: 'vendor',
        sort: false
      },
      {
        Header: 'Email Subject',
        accessor: 'emailSubject',
        sort: false
      },
      {
        Header: 'Email Body',
        accessor: 'emailBody',
        sort: false
      },
      {
        Header: 'Queued Date',
        accessor: 'createdOn',
        sort: false,
        overrideWidth: 200,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IACHBatchNotificationEmail } };
        }) => {
          return <span>{original.createdOn ? formatShortFriendlyDateWithTime(original.createdOn) : ''}</span>;
        }
      },
      {
        Header: 'Sent',
        accessor: 'sent',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IACHBatchNotificationEmail } };
        }) => {
          return <span>{original.sent ? 'Yes' : 'No'}</span>;
        }
      },
      {
        id: 'actions',
        Header: '',
        accessor: '',
        overrideWidth: 300,
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IACHBatchNotificationEmail } };
        }) => {
          return (
            <div className={classes.actionButtons}>
              <LoadingButton
                loading={isSending[original.batchNotificationEmailId]}
                loadingPosition="start"
                color="primary"
                startIcon={<Replay />}
                disabled={isSending[original.batchNotificationEmailId]}
                className={classes.resendButton}
                onClick={() => handleResendEmail(original.batchNotificationEmailId)}
              >
                Resend Email
              </LoadingButton>
              <LoadingButton
                loading={isDownloading[original.batchNotificationEmailId]}
                loadingPosition="start"
                color="secondary"
                startIcon={<Download />}
                disabled={isDownloading[original.batchNotificationEmailId]}
                className={classes.resendButton}
                onClick={() => handleDownloadPDF(original)}
              >
                PDF
              </LoadingButton>
            </div>
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSending, isDownloading]);

  const isMobile = useMediaQuery('(max-width: 960px)');
  return (
    <Accordion className={classes.root}>
      <AccordionSummary className={classes.accordion} expandIcon={<ExpandMore />} aria-controls="panel1a-content" id="panel1a-header">
        <div className={classes.topPanelSummaryWrapper}>
          <Typography className={classes.activeName}>Date: {formatDate(created)}</Typography>
          <Divider orientation="vertical" flexItem className={classes.divider} />
          <Box
            display="flex"
            alignItems={{
              xs: 'flex-start',
              sm: 'center'
            }}
            flexDirection={{
              xs: 'column',
              sm: 'row'
            }}
            width="100%"
            flexWrap="wrap"
          >
            <Typography color="primary" className={classes.boldName} sx={{ flex: 1 }}>
              Batch {batch} (created {formatDate(batchDate)}) - {totalVendors} vendor{totalVendors > 1 ? 's' : ''}, {totalPayments} payment{totalPayments > 1 ? 's' : ''},{' '}
              {formatMoney(batchTotal)} value
            </Typography>
            <LoadingButton
              loading={isSending[batchPaymentNotificationId]}
              loadingPosition="start"
              color="primary"
              startIcon={<Replay />}
              className={classes.resendButton}
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                handleResendBatchEmails(batchPaymentNotificationId);
              }}
            >
              Resend Batch
            </LoadingButton>
          </Box>
        </div>
      </AccordionSummary>
      <AccordionDetails className={classes.details}>
        <Table
          columns={columns}
          ResponsiveComponent={isMobile ? NotificationTableResponsiveComponent : undefined}
          data={batchEmails ?? []}
          isLoading={false}
          hidePagination
          noResultsText="No Notifications"
          mobileProps={{
            handleResendEmail,
            handleDownloadPDF,
            isSending,
            isDownloading
          }}
        />
      </AccordionDetails>
    </Accordion>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    maxWidth: '100%',
    border: `1px solid ${theme.palette.grey[300]}`,
    overflowX: 'hidden'
  },
  accordion: {
    padding: '0 16px',
    '&& .MuiAccordionSummary-expandIcon': {
      padding: 3
    }
  },
  topPanelSummaryWrapper: {
    marginTop: theme.spacing(0.5),
    display: 'flex',
    width: '100%',
    flexDirection: 'column',
    '@media (min-width: 600px)': {
      flexDirection: 'row',
      alignItems: 'center'
    },
    '&:first-child': {
      marginTop: 0
    }
  },
  panelSummaryWrapper: {
    '&:first-child': { marginTop: 0 }
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    padding: '12px 16px',
    backgroundColor: theme.palette.grey[100],
    width: '100%'
  },
  boldName: {
    color: theme.palette.primary.main,
    fontWeight: 600
  },
  divider: {
    margin: theme.spacing(0, 0.5)
  },
  activeName: {
    color: theme.palette.grey[500],
    [theme.breakpoints.up('sm')]: {
      width: '15%',
      minWidth: theme.spacing(5),
      maxWidth: theme.spacing(10)
    }
  },
  actionButtons: {
    textAlign: 'right'
  },
  resendButton: {
    textAlign: 'right',
    fontSize: 13
  }
}));
